import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronRight, X } from 'lucide-react'
import { TOUR_STEPS } from '../lib/tourSteps'
import { useTourAnchorRegistry } from '../contexts/TourAnchorContext'

// Spotlight tour. Renders a full-viewport overlay with a clipped-out
// hole around the current step's anchor element, plus a small
// popover card describing the feature. Steps are listed in
// src/lib/tourSteps.js; the controller walks them sequentially and
// navigates between routes when a step's `route` doesn't match the
// current location.
//
// Implementation:
// - Four absolutely-positioned divs around the anchor rect form
//   the "wall" of the overlay; the gap between them is the spotlight
//   hole. SVG mask was the alternative but four divs are cheaper to
//   animate and don't blow up Safari's compositor.
// - The popover anchors below the rect by default; if there isn't
//   enough room below the screen edge, it flips above. Direction
//   from the step ('top' / 'bottom') wins over auto when set.
// - Rect updates re-run on scroll / resize / orientation change.
//   We deliberately do NOT update on every animation frame — the
//   anchor only moves if the page reflows.
const PADDING = 8           // px of padding around the spotlight hole
const POPOVER_GAP = 12      // px between the spotlight and the popover

export default function SpotlightTour({ open, onFinish, onSkip }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { getAnchor } = useTourAnchorRegistry()
  const [stepIdx, setStepIdx] = useState(0)
  const [rect, setRect] = useState(null)
  const [popoverPlacement, setPopoverPlacement] = useState('bottom')
  const popoverRef = useRef(null)

  const step = TOUR_STEPS[stepIdx] || null
  const isLast = stepIdx >= TOUR_STEPS.length - 1

  // Navigate to the step's required route before measuring. A short
  // raf delay gives the new route a frame to mount its anchors;
  // without it, getAnchor() returns the previous step's element
  // for one paint.
  useEffect(() => {
    if (!open || !step) return undefined
    if (step.route && location.pathname !== step.route) {
      navigate(step.route)
    }
    // Reset rect so we don't render last step's hole during the
    // route swap. Measurement runs in the layout effect below.
    setRect(null)
  }, [open, step, location.pathname, navigate])

  // Measurement. useLayoutEffect so we read the DOM synchronously
  // after React commits — avoids a flicker where the hole would
  // animate from the previous rect to the new one.
  const measure = useCallback(() => {
    if (!step) return
    const el = getAnchor(step.anchor)
    if (!el) return
    const r = el.getBoundingClientRect()
    setRect({
      top: r.top - PADDING,
      left: r.left - PADDING,
      width: r.width + PADDING * 2,
      height: r.height + PADDING * 2,
      anchorTop: r.top,
      anchorBottom: r.bottom,
    })
  }, [step, getAnchor])

  useLayoutEffect(() => {
    if (!open || !step) return undefined
    // Retry a few times because the anchor may not be in the DOM
    // immediately after a route navigation. 4 frames is enough for
    // every page on this app — well under the eye's flicker
    // threshold (~16ms × 4 = 64ms).
    let cancelled = false
    let frames = 0
    const tick = () => {
      if (cancelled) return
      measure()
      frames += 1
      if (frames < 6) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
    return () => { cancelled = true }
  }, [open, step, measure, stepIdx, location.pathname])

  // Re-measure on window resize / scroll / orientation flip. We
  // throttle via rAF so a fast scroll doesn't fire setState a
  // hundred times.
  useEffect(() => {
    if (!open) return undefined
    let scheduled = false
    const handler = () => {
      if (scheduled) return
      scheduled = true
      requestAnimationFrame(() => {
        scheduled = false
        measure()
      })
    }
    window.addEventListener('resize', handler)
    window.addEventListener('orientationchange', handler)
    document.addEventListener('scroll', handler, { capture: true, passive: true })
    return () => {
      window.removeEventListener('resize', handler)
      window.removeEventListener('orientationchange', handler)
      document.removeEventListener('scroll', handler, { capture: true })
    }
  }, [open, measure])

  // Decide popover placement based on the step's `align` and the
  // available room. 'auto' (default) picks whichever side has more
  // viewport below/above the anchor.
  useLayoutEffect(() => {
    if (!rect || !step) return
    const explicit = step.align
    if (explicit === 'top' || explicit === 'bottom') {
      setPopoverPlacement(explicit)
      return
    }
    // Auto: pick whichever side has more room. Bottom wins ties.
    const roomBelow = window.innerHeight - rect.anchorBottom
    const roomAbove = rect.anchorTop
    setPopoverPlacement(roomBelow >= roomAbove ? 'bottom' : 'top')
  }, [rect, step])

  const next = useCallback(() => {
    if (isLast) onFinish?.()
    else setStepIdx((i) => Math.min(TOUR_STEPS.length - 1, i + 1))
  }, [isLast, onFinish])

  const prev = useCallback(() => {
    setStepIdx((i) => Math.max(0, i - 1))
  }, [])

  // Geometry for the popover. Declared BEFORE the early-return below
  // so the hook count stays invariant when `open` flips. Anchored
  // relative to the rect's outer edges so the spotlight padding
  // doesn't squeeze the popover against the highlighted element.
  const popoverStyle = useMemo(() => {
    if (!rect) {
      // No measurement yet — center fallback so the user doesn't
      // see an empty scrim.
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: 'min(360px, calc(100vw - 32px))',
      }
    }
    if (typeof window === 'undefined') return null
    const left = Math.max(
      16,
      Math.min(rect.left + rect.width / 2 - 180, window.innerWidth - 376),
    )
    if (popoverPlacement === 'top') {
      return {
        bottom: window.innerHeight - rect.top + POPOVER_GAP,
        left,
        maxWidth: 'min(360px, calc(100vw - 32px))',
      }
    }
    return {
      top: rect.top + rect.height + POPOVER_GAP,
      left,
      maxWidth: 'min(360px, calc(100vw - 32px))',
    }
  }, [rect, popoverPlacement])

  if (!open || !step || typeof document === 'undefined') return null

  return createPortal(
    <div className="fixed inset-0 z-[120] pointer-events-none" aria-hidden="false">
      {/* Four walls forming the spotlight hole. Each wall captures
          pointer events so clicks outside the highlight don't fall
          through to the underlying UI mid-tour. */}
      {rect ? (
        <>
          <div
            className="absolute bg-black/60 pointer-events-auto transition-all"
            style={{ top: 0, left: 0, right: 0, height: rect.top }}
          />
          <div
            className="absolute bg-black/60 pointer-events-auto transition-all"
            style={{ top: rect.top + rect.height, left: 0, right: 0, bottom: 0 }}
          />
          <div
            className="absolute bg-black/60 pointer-events-auto transition-all"
            style={{ top: rect.top, left: 0, width: rect.left, height: rect.height }}
          />
          <div
            className="absolute bg-black/60 pointer-events-auto transition-all"
            style={{
              top: rect.top,
              left: rect.left + rect.width,
              right: 0,
              height: rect.height,
            }}
          />
          {/* Highlight ring around the hole. */}
          <div
            className="absolute rounded-lg pointer-events-none ring-2 ring-app-primary-500 transition-all"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            }}
          />
        </>
      ) : (
        // Pre-measurement: solid scrim so the user sees we're
        // transitioning between steps.
        <div className="absolute inset-0 bg-black/60 pointer-events-auto" />
      )}

      {/* Popover card */}
      <div
        ref={popoverRef}
        className="absolute pointer-events-auto bg-card border border-border rounded-lg shadow-xl p-4 space-y-3 w-[360px]"
        style={popoverStyle}
        dir="auto"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start gap-2">
          <span className="text-[10px] uppercase tracking-wider text-app-primary-500 font-semibold flex-shrink-0">
            {t('tour.stepCounter', { current: stepIdx + 1, total: TOUR_STEPS.length })}
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onSkip}
            aria-label={t('tour.skipAria')}
            className="p-1 -m-1 rounded-md text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="text-base font-bold text-foreground" dir="auto">
          {t(step.titleKey)}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed" dir="auto">
          {t(step.bodyKey)}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="text-xs text-muted-foreground hover:text-foreground"
            dir="auto"
          >
            {t('tour.skip')}
          </button>
          <div className="flex-1" />
          {stepIdx > 0 && (
            <button
              type="button"
              onClick={prev}
              className="px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-accent"
              dir="auto"
            >
              {t('tour.back')}
            </button>
          )}
          <button
            type="button"
            onClick={next}
            className="px-3 py-1.5 rounded-md bg-app-primary-500 text-white text-sm font-semibold inline-flex items-center gap-1"
            dir="auto"
          >
            <span>{isLast ? t('tour.finish') : t('tour.next')}</span>
            {!isLast && <ChevronRight className="w-3.5 h-3.5 rtl:-scale-x-100" />}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
